import type { Photo } from './types';

interface Props {
  photos: Photo[];
  onOpen: (index: number) => void;
}

/**
 * Mobile-only floating stack of photo thumbnails. The top three photos are
 * fanned out at slight angles to hint at a pile; tapping opens the lightbox
 * starting at the topmost photo. A small counter badge shows the total.
 *
 * Docks in the top-right corner where the SummaryPanel's mobile pill sits on
 * the left, so the two don't collide.
 */
export function PhotoStack({ photos, onOpen }: Props) {
  if (photos.length === 0) return null;

  const top = photos.slice(0, 3);

  return (
    <button
      type="button"
      onClick={() => onOpen(0)}
      aria-label={`View ${photos.length} photo${photos.length === 1 ? '' : 's'}`}
      className="fixed top-3 right-3 z-10 md:hidden pointer-events-auto focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-lg"
    >
      <span className="relative block h-12 w-12">
        {top.map((p, i) => {
          const rotation = i === 0 ? -6 : i === 1 ? 4 : 0;
          const offsetX = i === 0 ? -2 : i === 1 ? 2 : 0;
          const offsetY = i === 0 ? 1 : i === 1 ? 0 : -1;
          return (
            <img
              key={p.id}
              src={p.thumbUrl}
              alt=""
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover rounded-[7px] border-2 border-white shadow-md"
              style={{
                transform: `translate(${offsetX}px, ${offsetY}px) rotate(${rotation}deg)`,
                zIndex: top.length - i
              }}
            />
          );
        })}
      </span>
      <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-ink-900 text-white text-[9px] font-mono font-medium flex items-center justify-center shadow-md z-10">
        {photos.length}
      </span>
    </button>
  );
}
